/**
 * Parse a simple CSV of roll numbers and marks for teacher marks entry.
 * Expected columns: rollNum, marks (header optional).
 */

function splitCsvLine(line) {
    const parts = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            inQuotes = !inQuotes;
            continue;
        }
        if ((ch === "," || ch === "\t" || ch === ";") && !inQuotes) {
            parts.push(cur.trim());
            cur = "";
            continue;
        }
        cur += ch;
    }
    parts.push(cur.trim());
    return parts;
}

function isHeaderRow(cols) {
    const a = String(cols[0] || "").toLowerCase();
    const b = String(cols[1] || "").toLowerCase();
    return (
        a.includes("roll") ||
        a === "no" ||
        a === "#" ||
        b.includes("mark") ||
        b.includes("score") ||
        b.includes("percent")
    );
}

/**
 * @returns {{ rollNum: number, marks: number }[]}
 */
export function parseMarksCsv(text) {
    const raw = String(text || "")
        .replace(/^\uFEFF/, "")
        .trim();
    if (!raw) return [];

    const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const rows = [];

    for (let i = 0; i < lines.length; i++) {
        const cols = splitCsvLine(lines[i]);
        if (!cols.length) continue;
        if (i === 0 && isHeaderRow(cols)) continue;

        const rollNum = Number(String(cols[0]).replace(/[^\d]/g, ""));
        const marks = Number(String(cols[1] ?? "").replace(/[^\d.]/g, ""));
        if (!Number.isFinite(rollNum)) continue;
        if (!Number.isFinite(marks) || marks < 0 || marks > 100) continue;
        rows.push({ rollNum, marks: Math.round(marks) });
    }

    return rows;
}

/**
 * Merge CSV rows into marksMap keyed by student id.
 * @returns {{ marksMap: object, imported: number, unmatched: number[], invalid: number }}
 */
export function applyMarksCsvImport({ csvText, students, existingMarksMap = {} }) {
    const parsed = parseMarksCsv(csvText);
    const byRoll = new Map();
    for (const st of students) {
        const roll = Number(st.rollNum);
        if (Number.isFinite(roll)) byRoll.set(roll, st._id || st.id);
    }

    const marksMap = { ...existingMarksMap };
    let imported = 0;
    const unmatched = [];
    let invalid = 0;

    const seenRolls = new Set();
    for (const row of parsed) {
        if (seenRolls.has(row.rollNum)) continue;
        seenRolls.add(row.rollNum);

        const sid = byRoll.get(row.rollNum);
        if (!sid) {
            unmatched.push(row.rollNum);
            continue;
        }
        marksMap[sid] = String(row.marks);
        imported += 1;
    }

    invalid = parsed.length - imported - unmatched.length;

    return { marksMap, imported, unmatched, invalid, parsedCount: parsed.length };
}

export function buildMarksCsvTemplate(students) {
    const header = "rollNum,marks";
    const lines = students
        .slice()
        .sort((a, b) => (Number(a.rollNum) || 0) - (Number(b.rollNum) || 0))
        .map((st) => `${st.rollNum},`);
    return [header, ...lines].join("\n");
}

export function downloadTextFile(filename, content) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
